import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { getSafeValue } from '../../../utils/security';
import { sanitizeFleetIdentifier } from '../../../utils/FleetHelpers';
import { colors, fontSize } from '../../../global/variables';
import infoIcon from '../../../global/img/information_.svg';
import EVSelector from './EVSelector';
import {
  StyledEditor,
  StyledEditorBody,
  StyledGrid,
  StyledField,
  StyledLabel,
  StyledRequiredMark,
  StyledInput,
  StyledSelect,
  StyledFooter,
  StyledCancelButton,
  StyledSaveButton,
} from './FleetEditorStyled';

const { primaryTextColor, aqua, secondaryTextColor } = colors;
const { smaller, normal, small } = fontSize;

const StyledCheckboxRow = styled.label({
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: normal,
  color: primaryTextColor,
  cursor: 'pointer',
  marginBottom: '0.9rem',
});

const StyledEVSectionTitle = styled.div({
  fontSize: smaller,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 700,
  color: aqua,
  marginBottom: '0.5rem',
});

const StyledInfoIcon = styled.img({
  width: '1rem',
  height: '1rem',
  marginLeft: '0.3rem',
  cursor: 'help',
  opacity: 0.7,
  verticalAlign: 'middle',
});

const StyledEvAuthNotification = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.8rem',
  padding: '0.6rem 1rem',
  marginBottom: '0.9rem',
  borderRadius: '0.12rem',
  backgroundColor: 'rgba(255, 152, 0, 0.08)',
  border: '1px solid rgba(255, 152, 0, 0.4)',
  fontSize: small,
  color: secondaryTextColor,
  a: {
    fontSize: small,
    color: aqua,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    fontWeight: 700,
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

const FleetProfileEditor = ({
  isNew,
  profileName,
  fleetType,
  isElectricVehicle,
  evProfile,
  oAuth,
  apiKey,
  vehicleAmount,
  fleetTypeOptions,
  canFleetTypeBeElectric,
  showEvInfo,
  onSave,
  onCancel,
  onDraftChange,
  labels: {
    profileNameLabel,
    profileTypeLabel,
    electricVehicleLabel,
    electricVehicleInfoTooltip,
    evAuthErrorMessage,
    evAuthErrorContactUs,
    evContactLink,
    electricVehicleProfileSectionTitle,
    saveProfileLabel,
    cancelProfileLabel,
    profileNamePlaceholder,
  },
}) => {
  const [draftName, setDraftName] = useState(isNew ? '' : profileName);
  const [draftFleetType, setDraftFleetType] = useState(isNew ? 'car' : fleetType);
  const [draftIsElectricVehicle, setDraftIsElectricVehicle] = useState(
    isNew ? false : isElectricVehicle,
  );
  const [draftEvProfile, setDraftEvProfile] = useState(isNew ? null : evProfile);
  const [evAuthError, setEvAuthError] = useState(false);

  useEffect(() => {
    if (onDraftChange) onDraftChange({ name: draftName, fleetType: draftFleetType });
  }, [draftName, draftFleetType, onDraftChange]);

  const handleEVAuthError = useCallback(() => {
    setDraftIsElectricVehicle(false);
    setEvAuthError(true);
  }, []);

  useEffect(() => {
    if (onDraftChange) onDraftChange({ name: draftName, fleetType: draftFleetType });
  }, [draftName, draftFleetType, onDraftChange]);

  const onNameChange = useCallback(
    (e) => setDraftName(sanitizeFleetIdentifier(e.target.value)),
    [],
  );

  const onFleetTypeChange = useCallback(
    (e) => {
      const newFleetType = e.target.value;
      setDraftFleetType(newFleetType);
      if (draftIsElectricVehicle && !canFleetTypeBeElectric(newFleetType))
        setDraftIsElectricVehicle(false);
    },
    [draftIsElectricVehicle, canFleetTypeBeElectric],
  );

  const onElectricVehicleToggle = useCallback((e) => {
    setDraftIsElectricVehicle(e.target.checked);
    if (e.target.checked) setEvAuthError(false);
  }, []);

  const onEVProfileChange = useCallback(
    (data) => setDraftEvProfile((prev) => (data ? { ...data, charging: prev?.charging } : null)),
    [],
  );

  const onChargingChange = useCallback(
    (chargingData) => setDraftEvProfile((prev) => ({ ...prev, charging: chargingData })),
    [],
  );

  const isValid = useMemo(
    () => (draftName || '').trim().length > 0 && !!draftFleetType,
    [draftName, draftFleetType],
  );

  const onSaveClick = useCallback(() => {
    onSave({
      name: draftName,
      fleetType: draftFleetType,
      isElectricVehicle: draftIsElectricVehicle,
      evProfile: draftIsElectricVehicle ? draftEvProfile : null,
    });
  }, [onSave, draftName, draftFleetType, draftIsElectricVehicle, draftEvProfile]);

  return (
    <StyledEditor>
      <StyledEditorBody>
        <StyledGrid>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-profile-name">
              {profileNameLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledInput
              id="input-fleet-profile-name"
              type="text"
              placeholder={profileNamePlaceholder}
              value={draftName}
              onChange={onNameChange}
            />
          </StyledField>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-profile-type">
              {profileTypeLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledSelect
              id="input-fleet-profile-type"
              value={draftFleetType}
              onChange={onFleetTypeChange}
            >
              {Object.keys(fleetTypeOptions)
                .filter((typeID) => typeID !== 'electricVehicle')
                .map((typeID) => (
                  <option key={typeID} value={typeID}>
                    {getSafeValue(fleetTypeOptions, typeID)}
                  </option>
                ))}
            </StyledSelect>
          </StyledField>
        </StyledGrid>
        {canFleetTypeBeElectric(draftFleetType) && (
          <StyledCheckboxRow htmlFor="input-fleet-profile-ev">
            <input
              id="input-fleet-profile-ev"
              type="checkbox"
              checked={draftIsElectricVehicle}
              onChange={onElectricVehicleToggle}
            />
            {electricVehicleLabel}
            {showEvInfo && (
              <StyledInfoIcon
                src={infoIcon}
                alt="info"
                title={electricVehicleInfoTooltip}
              />
            )}
          </StyledCheckboxRow>
        )}
        {evAuthError && (
          <StyledEvAuthNotification>
            <span>{evAuthErrorMessage}</span>
            <a href={evContactLink} target="_blank" rel="noopener noreferrer">
              {evAuthErrorContactUs}
            </a>
          </StyledEvAuthNotification>
        )}
        {draftIsElectricVehicle && (
          <>
            <StyledEVSectionTitle>{electricVehicleProfileSectionTitle}</StyledEVSectionTitle>
            <EVSelector
              oAuth={oAuth}
              apiKey={apiKey}
              onEVProfileChange={onEVProfileChange}
              onAuthError={handleEVAuthError}
              initialProfile={draftEvProfile}
              charging={draftEvProfile?.charging}
              vehicleAmount={vehicleAmount}
              onChargingChange={onChargingChange}
            />
          </>
        )}
      </StyledEditorBody>
      <StyledFooter>
        <StyledCancelButton id="button-fleet-profile-cancel" type="button" onClick={onCancel}>
          {cancelProfileLabel}
        </StyledCancelButton>
        <StyledSaveButton
          id="button-fleet-profile-save"
          type="button"
          disabled={!isValid}
          onClick={onSaveClick}
        >
          {saveProfileLabel}
        </StyledSaveButton>
      </StyledFooter>
    </StyledEditor>
  );
};

export default FleetProfileEditor;
